import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { formatApiError } from "../lib/api";
import { Disc3, Truck } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-slate-50">
      {/* Left: brand */}
      <div className="hidden md:flex bg-slate-900 relative overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_top_right,_rgba(234,88,12,0.4),_transparent_50%)]" />
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-4 mb-8 bg-white/95 rounded-sm p-3 shadow-lg">
            <img src="/logo-rpi.jpg" alt="Royal Persada Indah" className="w-16 h-16 object-contain" />
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">PT</div>
              <div className="font-display font-black text-slate-900 text-lg leading-tight tracking-tight">
                Royal Persada<br/>Indah
              </div>
            </div>
          </div>
          <h1 className="text-white font-display text-5xl font-black leading-tight mb-6">
            Kontrol Penuh <br />
            <span className="text-orange-500">Setiap Ban</span> <br />
            di Armada Anda.
          </h1>
          <p className="text-slate-400 text-base leading-relaxed">
            Sistem monitoring armada PT. Royal Persada Indah — status kendaraan, logbook maintenance, biaya operasional & manajemen ban secara real-time.
          </p>
          <div className="mt-10 flex items-center gap-3 text-slate-300">
            <Truck className="w-5 h-5 text-orange-500" />
            <span className="font-mono text-sm">Trucking · Fleet · Tyre Ops</span>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo (visible when left panel hidden) */}
          <div className="md:hidden flex items-center gap-3 mb-6 pb-6 border-b border-slate-200">
            <img src="/logo-rpi.jpg" alt="Royal Persada Indah" className="w-12 h-12 object-contain" />
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">PT</div>
              <div className="font-display font-black text-slate-900 text-sm leading-tight">Royal Persada Indah</div>
            </div>
          </div>
          <div className="mb-8">
            <h2 className="font-display text-3xl font-black text-slate-900">Masuk</h2>
            <p className="text-slate-500 mt-1 text-sm">Masuk ke <b>RPI Fleet Monitor</b>.</p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="uppercase text-xs tracking-widest font-bold text-slate-700">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 h-12 border-slate-300 focus:border-orange-500 focus:ring-orange-500 rounded-sm"
                placeholder="nama@perusahaan.id"
              />
            </div>
            <div>
              <Label htmlFor="password" className="uppercase text-xs tracking-widest font-bold text-slate-700">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 h-12 border-slate-300 focus:border-orange-500 focus:ring-orange-500 rounded-sm"
                placeholder="••••••••"
              />
            </div>

            {err && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm" data-testid="login-error">
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase tracking-wider rounded-sm btn-industrial"
            >
              {loading ? "Memproses..." : "Masuk"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
